<script setup>
import { cloneDeep, isEmpty } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { nextTick, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted } from 'vue';
import { useModal } from 'vue-final-modal';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '~/auth/stores/auth.store';
import BiCreateRichTextWidget from '~/bi/components/bi-create-rich-text-widget.vue';
import BiCreateWidget from '~/bi/components/bi-create-widget.vue';
import BiDashboardSettingsModal from '~/bi/components/bi-dashboard-settings-modal.vue';
import BiDuplicateDashboardModal from '~/bi/components/bi-duplicate-dashboard-modal.vue';
import BiHtmlDashboard from '~/bi/components/bi-html-dashboard.vue';
import BiStandardDashboard from '~/bi/components/bi-standard-dashboard.vue';
import BiScheduleExportsModal from '~/bi/components/export/bi-schedule-exports-modal.vue';
import BiGlobalFiltersListing from '~/bi/components/filters/bi-global-filters/bi-global-filters-listing.vue';
import BIGlobalFiltersSettings from '~/bi/components/filters/bi-global-filters/bi-global-filters-settings.vue';
import { useBiExport } from '~/bi/composables/bi-export.composable';
import { useBiStore } from '~/bi/store/bi.store';
import HawkDeletePopup from '~/common/components/organisms/hawk-delete-popup.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import useEmitter from '~/common/composables/useEmitter';
import { useCommonStore } from '~/common/stores/common.store';
import { sleep } from '~/common/utils/common.utils';
import { load_js_css_file, removeLoadedFile } from '~/common/utils/load-script.util';
import DashboardExitConfirmationPopup from '~/dashboard/components/dashboard-exit-confirmation-popup.vue';

const props = defineProps({
  options: {
    type: Object,
    default: () => ({
      hide_side_bar: false,
      hide_dashboard_options: true,
      can_modify_resource: false,
      is_stand_alone: false,
      show_top_bar: true,
      is_fam_configure_filter: false,
      dashboard_label: 'Dashboard',
    }),
  },
});

const route = useRoute();
const router = useRouter();

const is_public = computed(() => !!route.meta?.public);

const { $t, $toast } = useCommonImports();
const emitter = useEmitter();

const auth_store = useAuthStore();
const bi_store = useBiStore();
const common_store = useCommonStore();
const { dashboards, sorted_dashboards, selected_dashboard, selected_dashboard_details, modified_widgets, current_grid_layout, is_fullscreen } = storeToRefs(bi_store);
const { current_organization } = storeToRefs(auth_store);
const { active_asset } = storeToRefs(common_store);

const { exportDashboard } = useBiExport();

const state = reactive({
  is_page_loading: false,
  is_dashboard_loading: false,
  is_editing: false,
  show_sidebar: false,
  is_exporting: false,
  is_saving: false,
  pending_print_export: false,
  error: null,
});

let export_abort_controller;

const create_widget = useModal({
  component: BiCreateWidget,
  attrs: {
    onClose() {
      create_widget.close();
    },
  },
});

const create_rich_text_widget = useModal({
  component: BiCreateRichTextWidget,
  attrs: {
    onClose() {
      create_rich_text_widget.close();
    },
  },
});

const schedule_exports_modal = useModal({
  component: BiScheduleExportsModal,
  attrs: {
    onClose() {
      schedule_exports_modal.close();
    },
  },
});

const dashboard_settings_modal = useModal({
  component: BiDashboardSettingsModal,
  attrs: {
    onClose() {
      dashboard_settings_modal.close();
    },
  },
});

const delete_dashboard = useModal({
  component: HawkDeletePopup,
  attrs: {
    onClose() {
      delete_dashboard.close();
    },
  },
});

const dashboard_exit_confirmation_modal = useModal({
  component: DashboardExitConfirmationPopup,
});

const { open: openBiGlobalFiltersSettings, close: closeBiGlobalFiltersSettings } = useModal({
  component: BIGlobalFiltersSettings,
  attrs: {
    onClose() {
      closeBiGlobalFiltersSettings();
    },
    onSave(data) {
      logger.log('SAVED', data);
    },
  },
});

const duplicate_dashboard_modal = useModal({
  component: BiDuplicateDashboardModal,
  attrs: {
    onClose() {
      duplicate_dashboard_modal.close();
    },
  },
});

const can_modify_reports = computed(() => {
  return auth_store.check_permission('modify_reports', route.params.asset_id);
});

// Standard dashboards are the terra/scan-report dashboards
const is_standard_dashboard = computed(() => selected_dashboard_details.value?.standard === true);
// HTML dashboards allow the users to upload HTML files and see them as a separate dashboard (enabled for RIL only)
const is_html_dashboard = computed(() => selected_dashboard_details.value?.widgets?.content?.is_html === true);

const is_sidebar_hidden = computed(() => props.options.hide_side_bar || state.show_sidebar);

const create_widget_menu_items = computed(() => {
  return [
    { label: 'Widget', on_click: () => createWidget() },
    { label: $t('Rich text'), on_click: () => createRichTextWidget() },
  ];
});

const has_applied_global_filters = computed(() => {
  return !isEmpty(bi_store.get_applied_filters_for_dashboard);
});

const dashboard_menu_items = computed(() => {
  return [
    ...(!is_standard_dashboard.value && !is_html_dashboard.value
      ? [
          { name: 'edit', label: $t('Edit widgets'), on_click: () => (state.is_editing = true), disabled: !can_modify_reports.value },
          {
            name: 'export',
            label: $t('Export PDF'),
            submenu_items: [
              { name: 'now', label: $t('Export PDF'), on_click: () => setExportingState(true), left_icon: IconHawkDownloadOne },
              { name: 'schedule', label: $t('Schedule exports'), on_click: () => scheduleDashboardExports(), left_icon: IconHawkClock },
            ],
          },
        ]
      : []),
    { name: 'duplicate', label: $t('Duplicate'), on_click: () => duplicateDashboard(), disabled: !can_modify_reports.value },
    { name: 'settings', label: $t('Settings'), on_click: () => modifyDashboardSettings(), disabled: !can_modify_reports.value },
    { name: 'delete', label: $t('Delete'), on_click: () => deleteDashboard(), disabled: !can_modify_reports.value },
  ];
});

function createDashboard() {
  dashboard_settings_modal.patchOptions({
    attrs: {
      mode: 'create',
      dashboard: {},
      async onSave(payload) {
        try {
          await bi_store.createDashboard(payload);
          dashboard_settings_modal.close();
        }
        catch (err) {
          logger.error('Failed to create dashboard:', err);
          $toast({
            title: $t('Failed to create dashboard'),
            text: $t('Please try again'),
            type: 'error',
          });
        }
      },
    },
  });
  dashboard_settings_modal.open();
}

function onSelectDashboard(dashboard) {
  if (state.is_editing) {
    dashboard_exit_confirmation_modal.patchOptions({
      attrs: {
        onClose() {
          dashboard_exit_confirmation_modal.close();
        },
        onConfirm: async () => {
          state.is_editing = false;
          selected_dashboard.value = dashboard.uid;
          dashboard_exit_confirmation_modal.close();
        },
      },
    });
    dashboard_exit_confirmation_modal.open();
  }
  else {
    selected_dashboard.value = dashboard.uid;
  }
}

function createWidget() {
  create_widget.patchOptions({
    attrs: {
      mode: 'create',
      view: 'data-builder',
      widgetBuilderData: [],
      config: {},
      onClose() {
        create_widget.close();
      },
    },
  });
  create_widget.open();
}

function createRichTextWidget() {
  create_rich_text_widget.open();
}

async function onSaveDashboard() {
  state.is_saving = true;

  try {
    const updated_widgets = current_grid_layout.value.map((layout_widget) => {
      const uid = layout_widget.uid;

      const created_widget = modified_widgets.value.created[uid];
      const updated_widget = modified_widgets.value.updated[uid];

      if (created_widget) {
        return {
          ...layout_widget,
          name: created_widget.name,
          config: created_widget.config,
        };
      }
      else if (updated_widget) {
        const existing_widget = bi_store.selected_dashboard_details.widgets.find(w => w.uid === uid);
        return {
          ...existing_widget,
          ...layout_widget,
          ...updated_widget,
        };
      }
      else {
        const existing_widget = bi_store.selected_dashboard_details.widgets.find(w => w.uid === uid);
        return {
          ...existing_widget,
          ...layout_widget,
        };
      }
    });

    await bi_store.updateDashboard(selected_dashboard.value, {
      report_data: updated_widgets,
    });
    modified_widgets.value.created = {};
    modified_widgets.value.updated = {};

    await nextTick();
    state.is_editing = false;
    $toast({
      title: $t('Dashboard saved'),
      text: $t('Your dashboard has been saved successfully'),
      type: 'success',
    });
  }
  catch (err) {
    console.error('Failed to save dashboard:', err);
    $toast({
      title: $t('Failed to save dashboard'),
      text: $t('Please try again'),
      type: 'error',
    });
  }
  finally {
    state.is_saving = false;
  }
}

function setExportingState(value) {
  state.is_exporting = value;
}

function cancelExport() {
  export_abort_controller.abort();
  setExportingState(false);
}

async function exportCurrentDashboard() {
  if (!bi_store.are_all_widgets_rendered) {
    await new Promise((resolve) => {
      const interval_id = setInterval(() => {
        if (bi_store.are_all_widgets_rendered) {
          clearInterval(interval_id);
          resolve();
        }
      }, 1000);
    });
  }
  await sleep(2000);

  export_abort_controller = new AbortController();
  await exportDashboard(current_grid_layout.value, export_abort_controller);
  export_abort_controller = null;
}

async function refreshDashboard() {
  state.is_dashboard_loading = true;
  await bi_store.fetchDashboard();
  current_grid_layout.value = cloneDeep(selected_dashboard_details.value.widgets);
  bi_store.resetRenderedWidgets();
  state.is_dashboard_loading = false;
}

function scheduleDashboardExports() {
  schedule_exports_modal.patchOptions({
    attrs: {
      scheduledExports: selected_dashboard_details.value?.scheduled_exports || [],
    },
  });
  schedule_exports_modal.open();
}

function duplicateDashboard() {
  duplicate_dashboard_modal.open();
}

function modifyDashboardSettings() {
  dashboard_settings_modal.patchOptions({
    attrs: {
      mode: 'edit',
      dashboard: selected_dashboard_details.value,
      async onSave(payload) {
        try {
          await bi_store.updateDashboard(selected_dashboard.value, payload);
          dashboard_settings_modal.close();
        }
        catch (err) {
          logger.error('Failed to modify dashboard settings:', err);
          $toast({
            title: $t('Failed to modify dashboard settings'),
            text: $t('Please try again'),
            type: 'error',
          });
        }
      },
    },
  });
  dashboard_settings_modal.open();
}

function deleteDashboard() {
  delete_dashboard.patchOptions({
    attrs: {
      header: `${$t('Delete dashboard')}: "${selected_dashboard_details.value.name}"`,
      content: $t('Are you sure you want to delete this dashboard?'),
      confirm: async () => {
        try {
          await bi_store.deleteDashboard(selected_dashboard.value);
          delete_dashboard.close();
          $toast({
            title: $t('Dashboard deleted'),
            text: $t('Your dashboard has been deleted successfully'),
            type: 'success',
          });
        }
        catch (err) {
          logger.error('Failed to delete dashboard:', err);
          $toast({
            title: $t('Failed to delete dashboard'),
            text: $t('Please try again'),
            type: 'error',
          });
        }
      },
    },
  });
  delete_dashboard.open();
}

function leaveDashboard(next) {
  dashboard_exit_confirmation_modal.patchOptions({
    attrs: {
      onClose() {
        window.history.forward();
        dashboard_exit_confirmation_modal.close();
        return false;
      },
      onConfirm: async () => {
        state.is_editing = false;
        dashboard_exit_confirmation_modal.close();
        next();
      },
    },
  });
  dashboard_exit_confirmation_modal.open();
}

function updateRouterQuery() {
  router.replace({
    ...route,
    query: {
      ...route.query,
      dashboard: selected_dashboard.value,
    },
  });
}

function onFullscreenClicked() {
  if (is_fullscreen.value) {
    document.exitFullscreen();
  }
  else {
    const element = document.getElementById('bi-fullscreen-container');
    if (element.requestFullscreen)
      element.requestFullscreen();

    else if (element.mozRequestFullScreen)
      element.mozRequestFullScreen();

    else if (element.webkitRequestFullscreen)
      element.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);

    else if (element.msRequestFullscreen)
      element.msRequestFullscreen();
  }
}

function onFullscreenToggled() {
  bi_store.set_is_fullscreen(!!document.fullscreenElement);
}

async function fetchSchema() {
  try {
    await bi_store.getSchemas();
  }
  catch (error) {
    logger.error(error);
    $toast({
      title: 'Something went wrong',
      text: 'Could not fetch datasets',
      type: 'error',
    });
  }
}

watch(() => selected_dashboard.value, async (new_val) => {
  if (is_public.value)
    return;
  if (new_val) {
    state.is_dashboard_loading = true;
    await bi_store.fetchDashboard();
    state.is_dashboard_loading = false;
    // NOTE: This cloneDeep here and the one in onMounted are added temporarily so that the reference is not carried forward (for safety). This can be removed later on based on the final requirements of how switching b/w dashboards should work.
    current_grid_layout.value = cloneDeep(dashboards.value[new_val].widgets);
    updateRouterQuery();
    bi_store.applyDefaultFilters();
    bi_store.resetRenderedWidgets();
  }
}, { deep: true });

watch(() => [current_organization.value?.uid, route.params.asset_id], async (newVal, oldVal) => {
  if (is_public.value)
    return;
  if (!oldVal || newVal[1] !== oldVal[1]) {
    router.go();
  }
});

watch(() => bi_store.are_all_widgets_rendered, async (all_rendered) => {
  if (all_rendered && state.pending_print_export) {
    state.pending_print_export = false;
    await sleep(20000);
    setExportingState(true);
  }
});

onBeforeRouteLeave((_to, _from, next) => {
  if (state.is_editing)
    leaveDashboard(next);
  else next();
});

onBeforeMount(() => {
  state.is_page_loading = true;
});

async function initializePublic() {
  // public_token is present in the dashboard build when dashboard is shared through a public URL.
  if (!route.query.public_token || !route.query.organization) {
    state.error = $t('Invalid or missing public token / organization.');
    state.is_page_loading = false;
    return;
  }

  try {
    if (route.params.asset_id)
      bi_store.set_scope('micro_app');
    else
      bi_store.set_scope('global');

    if (!route.query.dashboard) {
      state.error = $t('Dashboard not found.');
      return;
    }

    selected_dashboard.value = route.query.dashboard;

    const asset_id = route.params.asset_id;
    const asset_query = asset_id ? { asset: asset_id, asset_uid: asset_id } : {};
    await Promise.all([bi_store.fetchPublicDashboard(route.query.public_token, asset_query), fetchSchema()]);

    if (!selected_dashboard_details.value) {
      state.error = $t('Dashboard not found.');
      return;
    }

    if (selected_dashboard_details.value?.widgets)
      current_grid_layout.value = cloneDeep(selected_dashboard_details.value.widgets);
  }
  catch (err) {
    logger.error('Failed to load public dashboard:', err);
    state.error = $t('Failed to load dashboard. The link may have expired or been revoked.');
    $toast({ title: $t('Error'), text: state.error, type: 'error' });
  }
  finally {
    state.is_page_loading = false;
  }
}

onMounted(async () => {
  if (is_public.value) {
    await initializePublic();
    document.addEventListener('fullscreenchange', onFullscreenToggled);
    return;
  }

  // micro_app is the dashboard build of the application
  if (props.options.is_stand_alone)
    bi_store.set_scope('micro_app');
  else
    bi_store.set_scope('global');

  await fetchSchema();

  await bi_store.initializeStore();

  if (route?.query?.print && route?.query?.dashboard && dashboards.value[route.query.dashboard]) {
    const current_dashboard = dashboards.value[route.query.dashboard];
    if (current_dashboard.scheduled_exports.find(export_config => export_config.uid === route.query.scheduled_export))
      state.pending_print_export = true;
  }

  if (route.query.dashboard && dashboards.value[route.query.dashboard]) {
    selected_dashboard.value = dashboards.value[route.query.dashboard].uid;
  }
  else {
    selected_dashboard.value = sorted_dashboards.value[0]?.uid;
    updateRouterQuery();
  }

  if (selected_dashboard.value && selected_dashboard_details.value) {
    current_grid_layout.value = cloneDeep(selected_dashboard_details.value.widgets);
  }

  bi_store.resetRenderedWidgets();

  if (route?.query?.scheduled_export) {
    bi_store.clearAllAppliedGlobalFilters();
    const export_item = selected_dashboard_details.value.scheduled_exports.find(export_config => export_config.uid === route.query.scheduled_export);
    if (export_item) {
      Object.keys(export_item.filters).forEach((filter_key) => {
        bi_store.updateAppliedGlobalFilter(filter_key, export_item.filters[filter_key]);
      });
    }
  }

  state.is_page_loading = false;

  document.addEventListener('fullscreenchange', onFullscreenToggled);

  emitter.on('bi:navigate-to-dashboard', (dashboard_uid) => {
    onSelectDashboard({ uid: dashboard_uid });
  });
});

onBeforeUnmount(() => {
  ['bi-handsontable-theme-css', 'bi-handsontable-css'].forEach(removeLoadedFile);
  document.removeEventListener('fullscreenchange', onFullscreenToggled);
});

onUnmounted(() => {
  emitter.off('bi:navigate-to-dashboard');
  bi_store.$reset();
});

// TODO: Update this
// Temporary compatibility logic.
// It will be removed after all remaining implementations migrate to the updated Handsontable version.
watch(() => route.query, (new_query, old_query) => {
  if (new_query?.form && !old_query?.form) {
    removeLoadedFile('bi-handsontable-css');
    removeLoadedFile('bi-handsontable-theme-css');
  }
  if (!new_query?.form && old_query?.form) {
    removeLoadedFile('handsontable-css');
    load_js_css_file(
      'https://cdn.jsdelivr.net/npm/handsontable@16.1.0/styles/handsontable.min.css',
      'bi-handsontable-css',
      'css',
    );
    load_js_css_file(
      'https://cdn.jsdelivr.net/npm/handsontable@16.1.0/styles/ht-theme-main.css',
      'bi-handsontable-theme-css',
      'css',
    );
  }
});
</script>

<template>
  <div>
    <HawkLoader v-if="state.is_page_loading" />
    <div v-else-if="state.error" class="flex flex-col items-center justify-center min-h-screen gap-4 text-center px-6">
      <IconIllustrationBiDashboards class="mb-2 opacity-50" />
      <p class="text-lg font-semibold text-gray-800">
        {{ $t('Unable to load dashboard') }}
      </p>
      <p class="text-sm text-gray-500">
        {{ state.error }}
      </p>
    </div>
    <div v-else class="flex w-full items-start">
      <DashboardSidebar v-if="!props.options.hide_side_bar && !is_public" :show_sidebar="state.show_sidebar" class="top-[65px]" />
      <div v-if="Object.keys(dashboards || {}).length" class="w-full">
        <div v-if="is_public" class="sticky top-0 z-[998] bg-white border-b border-gray-200 px-4 py-3">
          <h1 class="text-base font-semibold text-gray-800">
            {{ selected_dashboard_details?.name }}
          </h1>
        </div>
        <div v-else class="sticky z-[998] bg-white" :class="!props.options.is_stand_alone ? 'top-[65px]' : 'top-0'">
          <div class="px-4 pt-3 flex items-center gap-3">
            <HawkButton
              v-if="!props.options.hide_side_bar && active_asset"
              icon
              type="text"
              class="-mt-3 -ml-2 mr-2"
              @click="state.show_sidebar = !state.show_sidebar"
            >
              <IconHawkMenuThree />
            </HawkButton>
            <HawkTabs
              :tabs="sorted_dashboards.map(item => ({ uid: item.uid, label: item.name }))"
              :active_item="selected_dashboard"
              container_class="border-transparent"
              :character_length="20"
              @tab-click="onSelectDashboard"
            />
            <HawkButton
              v-if="!props.options.is_stand_alone"
              icon
              type="text"
              class="-mt-3"
              @click="createDashboard"
            >
              <IconHawkPlus />
            </HawkButton>
          </div>
          <hr>
        </div>
        <div id="bi-fullscreen-container" :class="is_fullscreen ? 'overflow-auto' : ''">
          <div
            class="sticky z-10 bg-gray-50 bi-dashboard-toolbar"
            :class="{
              'top-0': is_fullscreen,
              'top-[114px]': !is_fullscreen && !props.options.is_stand_alone && !is_public,
              'top-[49px]': !is_fullscreen && (props.options.is_stand_alone || is_public),
            }"
          >
            <div class="px-4 py-2 flex justify-between items-center min-h-[46px]">
              <div class="flex flex-wrap gap-2 items-center">
                <BiGlobalFiltersListing v-if="selected_dashboard_details?.meta?.global_filters?.length" />
                <HawkButton v-if="state.is_editing" icon type="text" class="hover:!bg-gray-200" @click="openBiGlobalFiltersSettings">
                  <IconHawkSettingsOne />
                </HawkButton>
                <HawkButton v-if="!state.is_editing && has_applied_global_filters" icon class="!bg-gray-300 !text-black !rounded-full !h-6 !w-6 bi-non-exportable" @click="() => bi_store.clearAllAppliedGlobalFilters()">
                  <IconHawkXClose class="w-4 h-4" />
                </HawkButton>
              </div>
              <div class="flex items-center gap-1">
                <HawkButton
                  v-if="!state.is_editing"
                  v-tippy="{
                    content: $t('Refresh'),
                    placement: 'bottom',
                  }"
                  icon
                  type="text"
                  class="bi-non-exportable hover:!bg-gray-200"
                  @click="refreshDashboard"
                >
                  <IconHawkRefreshCwOne class="!text-gray-600" />
                </HawkButton>
                <HawkButton
                  v-if="(!props.options.is_stand_alone || is_public) && !state.is_editing && selected_dashboard_details.widgets?.length"
                  v-tippy="{
                    content: $t('Export PDF'),
                    placement: 'bottom',
                  }"
                  icon
                  type="text"
                  class="bi-non-exportable hover:!bg-gray-200"
                  @click="setExportingState(true)"
                >
                  <IconHawkPrinter class="!text-gray-600" />
                </HawkButton>
                <template v-if="!props.options.is_stand_alone || is_public">
                  <HawkButton
                    v-if="!state.is_editing && selected_dashboard_details.widgets?.length"
                    v-tippy="{
                      content: $t('Fullscreen mode'),
                      placement: 'bottom',
                    }"
                    icon
                    type="text"
                    class="bi-non-exportable hover:!bg-gray-200"
                    @click="onFullscreenClicked"
                  >
                    <IconHawkMaximizeTwo />
                  </HawkButton>
                  <template v-if="!is_fullscreen && !is_public">
                    <div v-if="state.is_editing" class="flex items-center gap-3">
                      <HawkMenu :items="create_widget_menu_items" position="fixed" additional_trigger_classes="!ring-0">
                        <template #trigger="{ open }">
                          <HawkButton type="outlined">
                            {{ $t('Add') }}
                            <IconHawkChevronDown v-if="!open" />
                            <IconHawkChevronUp v-else />
                          </HawkButton>
                        </template>
                      </HawkMenu>
                      <HawkButton :loading="state.is_saving" @click="onSaveDashboard">
                        <IconHawkRocketTwo />
                        {{ $t('Publish') }}
                      </HawkButton>
                    </div>
                    <div v-else class="flex items-center bi-non-exportable">
                      <HawkMenu :items="dashboard_menu_items">
                        <template #trigger>
                          <HawkButton icon type="text" class="hover:!bg-gray-200">
                            <IconHawkSettingsOne />
                          </HawkButton>
                        </template>
                        <template #item="{ item }">
                          <div v-if="!item.submenu_items" class="flex items-center text-sm font-medium text-gray-700">
                            {{ item.label }}
                          </div>
                          <HawkMenu
                            v-else
                            :items="item.submenu_items"
                            class="flex items-center w-full"
                            additional_trigger_classes="w-full !ring-0"
                            additional_dropdown_classes="right-[112%] !-top-[20px] !bottom-auto"
                            additional_item_classes="w-[180px]"
                            position="bottom-left"
                          >
                            <template #trigger>
                              <div class="flex justify-between w-full py-3 -my-3">
                                <div class="flex items-center text-sm font-medium text-gray-700">
                                  {{ item.label }}
                                </div>
                                <IconHawkChevronRight class="text-gray-600" />
                              </div>
                            </template>
                            <template #item="{ item: submenu_item }">
                              <div class="flex items-center text-sm font-medium text-gray-700">
                                <component :is="submenu_item.left_icon" v-if="submenu_item.left_icon" class="inline mr-3 text-gray-500" />
                                {{ submenu_item.label }}
                              </div>
                            </template>
                          </HawkMenu>
                        </template>
                      </HawkMenu>
                    </div>
                  </template>
                </template>
              </div>
            </div>
            <hr>
          </div>
          <HawkLoader v-if="state.is_dashboard_loading" container_class="bg-gray-50 min-h-[calc(100vh-167px)]" />
          <BiStandardDashboard v-else-if="is_standard_dashboard" :key="selected_dashboard" :is-sidebar-hidden="is_sidebar_hidden" />
          <BiHtmlDashboard v-else-if="is_html_dashboard" :key="selected_dashboard" />
          <div v-else-if="current_grid_layout?.length === 0 && !state.is_editing" class="w-full flex justify-center items-center text-sm font-semibold">
            <div class="w-full flex items-center justify-center min-h-[calc(100vh-167px)] bg-gray-50">
              <div class="flex flex-col justify-center items-center">
                <IconIllustrationBiDashboards class="mb-4" />
                <div class="text-sm font-semibold text-gray-900">
                  {{ $t('No Widgets') }}
                </div>
                <div class="text-sm font-normal text-gray-600">
                  {{ $t('no-data-dashboard-widgets') }}
                </div>
              </div>
            </div>
          </div>
          <BiGrid v-else-if="current_grid_layout?.length > 0" :is-editing="state.is_editing" />
        </div>
      </div>
      <div v-else class="w-full flex items-center justify-center min-h-[calc(100vh-65px)] bg-gray-50">
        <div class="flex flex-col justify-center items-center">
          <IconIllustrationBiDashboards class="mb-4" />
          <div class="text-sm font-semibold text-gray-900">
            {{ $t('No Dashboards') }}
          </div>
          <div class="text-sm font-normal text-gray-600">
            {{ $t('no-data-bi-dashboards') }}
          </div>
          <HawkButton v-if="!is_public" class="mt-8" @click="createDashboard">
            {{ $t('Create dashboard') }}
          </HawkButton>
        </div>
      </div>
      <HawkExportToast
        v-if="state.is_exporting"
        :submit="exportCurrentDashboard"
        :progress_text="$t('Exporting PDF')"
        :completed_text="$t('Exported PDF')"
        class="z-[1000]"
        @cancel="cancelExport"
        @close="setExportingState(false)"
      />
    </div>
  </div>
</template>
